import React, { useEffect, useState, useRef, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { AppboxoWebSDK } from '@appboxo/web-sdk'
import type {
  PaymentRequest,
  PaymentResponse,
  PaymentStatus,
} from '@appboxo/web-sdk'

import { BaseView, BaseText } from '@/components/base'
import { LoadingSpinner, StickyHeaderNavigation } from '@/components'
import { useTheme } from '@/hooks/useTheme'
import useOffsets from '@/hooks/useOffsets'
import useLayoutStore from '@/store/useLayoutStore'
import config from '@/config/env'
import { boxoAuth } from '@/domain/boxo'
import { detectTelegramWebView } from '@/utils/telegram'
import { useAppAnalytics } from '@/services/analytics'

const BOXO_BACKGROUND_COLOR = '#171819'

const PaymentStatusValues: Record<
  'Success' | 'Failed' | 'Cancelled',
  PaymentStatus
> = {
  Success: 'success',
  Failed: 'failed',
  Cancelled: 'cancelled',
}

function createPaymentResponse(
  paymentData: PaymentRequest,
  status: PaymentStatus,
  hostappOrderId: string,
): PaymentResponse {
  return {
    ...paymentData,
    status,
    hostappOrderId,
  }
}

const HEADER_HEIGHT = 56

export const BoxoMiniappScreen: React.FC = () => {
  const { appId } = useParams<{ appId: string }>()
  const navigate = useNavigate()
  const theme = useTheme()
  const offsets = useOffsets()
  const setScreenBackground = useLayoutStore(
    (state) => state.setScreenBackground,
  )

  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const containerRef = useRef<HTMLDivElement>(null)
  const sdkRef = useRef<AppboxoWebSDK | null>(null)
  const initRef = useRef(false)
  const { trackEvent, events } = useAppAnalytics()

  const isTelegramWebView = detectTelegramWebView()

  // Set dark background for boxo miniapp screen
  useEffect(() => {
    trackEvent(events.ESIM_STARTED, {})
    setScreenBackground(BOXO_BACKGROUND_COLOR)
    return () => {
      setScreenBackground(null)
    }
  }, [setScreenBackground, trackEvent, events])

  // Container style that fills the screen below the header
  const containerStyle = useMemo(
    () => ({
      position: 'fixed' as const,
      top: `${HEADER_HEIGHT + offsets.top}px`,
      left: 0,
      right: 0,
      bottom: 0,
      overflow: 'hidden' as const,
      display: isLoading ? 'none' : 'block',
      background: '#171819',
    }),
    [offsets.top, isLoading],
  )

  const handleBack = () => {
    if (sdkRef.current) {
      sdkRef.current.destroy()
    }
    navigate(-1)
  }

  useEffect(() => {
    if (initRef.current || !appId) {
      return
    }
    initRef.current = true

    const clientId = config.VITE_BOXO_CLIENT_ID
    const isDebug = config.VITE_BOXO_DEBUG

    if (!clientId) {
      setError('Boxo client ID is not configured')
      setIsLoading(false)
      return
    }

    const boxoSdk = new AppboxoWebSDK({
      clientId,
      appId,
      debug: isDebug,
      isDesktop: true,
      // onPaymentRequest: async (
      //   paymentData: PaymentRequest,
      // ): Promise<PaymentResponse> => {
      //   console.log('[Boxo Payment] Payment request:', paymentData)

      //   // TODO: Implement actual payment processing through backend
      //   // For now, simulate a successful payment after a delay
      //   await new Promise((resolve) => setTimeout(resolve, 1000))
      //   return createPaymentResponse(
      //     paymentData,
      //     PaymentStatusValues.Success,
      //     `order_${Date.now()}`,
      //   )
      // },
    })

    boxoSdk.onPaymentComplete((success: boolean, data?: any) => {
      if (success) {
        trackEvent(events.ESIM_COMPLETED, {
          plan_name: data?.title || data?.description || 'unknown',
          price: data?.amount ? Number(data.amount) : 0,
          currency: data?.currency || 'USD',
        })
      }
    })

    sdkRef.current = boxoSdk

    const mountMiniapp = async () => {
      if (!containerRef.current) {
        setIsLoading(false)
        return
      }

      try {
        setError(null)
        const iframe = await boxoSdk.mount({
          container: containerRef.current,
          className: 'boxo-miniapp-iframe',
        })
        setIsLoading(false)

        // Register onAuth hook AFTER mount
        boxoSdk.onAuth(async () => {
          try {
            // Request tokens from backend
            const authResult = await boxoAuth({ miniapp_id: appId })
            boxoSdk.setAuthTokens(authResult.token, authResult.refresh_token)
          } catch (authError) {
            console.error('[Boxo Auth] Failed to get auth tokens:', authError)
          }
        })

        boxoSdk.onLoginComplete((success: boolean, data?: any) => {
          // Login complete callback
        })
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Unknown error'
        setError(errorMsg)
        setIsLoading(false)
      }
    }

    // Wait for container to be ready
    setTimeout(() => {
      mountMiniapp()
    }, 100)

    return () => {
      boxoSdk.destroy()
    }
  }, [appId, isTelegramWebView])

  const handleExitPress = () => {
    navigate('/community')
  }

  if (error) {
    return (
      <BaseView flex={1} bgColor="#171819">
        <StickyHeaderNavigation
          title="Error"
          // onBackPress={handleBack}
          showExitButton={true}
          showBackButton={false}
          onExitPress={handleExitPress}
          showBackground={false}
          containerStyle={{ background: '#171819' }}
          exitButtonStyle={{
            position: 'absolute',
            right: '5px',
            top: '65px',
            transform: 'translateY(-50%)',
            zIndex: 10,
          }}
        />
        <BaseView flex={1} center px={16}>
          <BaseText
            fontSize={18}
            fontFamily="semibold"
            color="label.default"
            textAlign="center"
            style={{ marginBottom: 8 }}
          >
            Failed to load mini-app
          </BaseText>
          <BaseText
            fontSize={16}
            fontFamily="regular"
            color="label.description"
            textAlign="center"
            style={{ marginBottom: 24 }}
          >
            {error}
          </BaseText>
          <BaseView
            py={12}
            px={24}
            style={{
              backgroundColor: theme.colors.background.buttonPrimaryEnabled,
              borderRadius: '12px',
              cursor: 'pointer',
            }}
            onClick={handleBack}
          >
            <BaseText
              fontSize={16}
              fontFamily="medium"
              color="label.buttonPrimaryEnabled"
            >
              Go Back
            </BaseText>
          </BaseView>
        </BaseView>
      </BaseView>
    )
  }

  return (
    <BaseView flex={1} bgColor="#171819">
      <StickyHeaderNavigation
        title=""
        // onBackPress={handleBack}
        showBackButton={false}
        showExitButton={true}
        onExitPress={handleExitPress}
        showBackground={false}
        containerStyle={{ background: '#171819' }}
        exitButtonStyle={{
          position: 'absolute',
          right: '5px',
          top: '65px',
          transform: 'translateY(-50%)',
          zIndex: 10,
        }}
      />

      {isLoading && (
        <BaseView
          style={{
            position: 'fixed',
            top: `${HEADER_HEIGHT + offsets.top}px`,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#171819',
          }}
        >
          <LoadingSpinner size="large" />
        </BaseView>
      )}

      <div ref={containerRef} style={containerStyle} />

      <style>{`
        .boxo-miniapp-iframe {
          width: 100% !important;
          height: 100% !important;
          border: none !important;
          background: #171819;
        }
      `}</style>
    </BaseView>
  )
}

export default BoxoMiniappScreen
